import { DerbyClock, type ClockFrame } from '../fixed-step';
import { DerbyRngV1, hashFnv1a } from '../rng';
import { gradeFor, runSession, xpFor, type MinigameId } from '../minigames/framework';
import { MINIGAMES, MINIGAMES_FOR_FACILITY, facilityForMinigame } from '../minigames';
import type { StatId } from '../race/types';
import { statsToParams, theoreticalBestTime } from '../race/params';
import type { RanchCommand } from './commands';
import {
  DERBY_LEAGUE,
  ENTRY_HORIZON_DAYS,
  FACILITY_DEFS,
  FEED_PLANS,
  LEAGUE_ECONOMY,
  STABLE_DEF,
  START_MONEY_CENTS,
  eventDay,
  eventLeague,
  isValidEvent,
  purseFor,
  facilityForStat,
  classAdjustedStats,
  RACE_CLASSES,
  stableStalls,
  type FacilityId,
} from './economy';
import {
  FORCED_RETIREMENT_AGE,
  agePotentialCap,
  ageOf,
  conceiveFoal,
  foundationHorse,
  horseFromGenotype,
  horseValueCents,
  makeStuds,
  rollYearlings,
  sanitizeName,
  seasonOf,
  starterName,
  studFeeCents,
} from './horse';
import { xpToNext } from './horse';
import { makeReplacementRival, makeRivals, rivalStats } from './rivals';
import {
  TRAIT_DEFS,
  expressedTraits,
  sessionMods,
  trainingEnergyCost,
  trainingXpMult,
  type TraitId,
} from './traits';
import type { Horse, HorseId, RanchEvent, RanchState, RejectReason } from './types';

export const RANCH_TICK_S = 0.1;
const MAX_STEPS_PER_FRAME = 40;
const EVENT_CAP = 256;
const RACE_LOG_CAP = 50;
export const STAT_HARD_CAP = 20;

/**
 * The ranch simulation. Ambient 10 Hz tick for future animation timers;
 * everything that matters advances by command — the calendar included.
 * Zero pixi/DOM imports; a career is replayable from its command tape.
 */
export class RanchSim {
  readonly state: RanchState;

  private readonly clock = new DerbyClock(RANCH_TICK_S, MAX_STEPS_PER_FRAME);
  private readonly queue: RanchCommand[] = [];
  private readonly events: RanchEvent[] = [];

  private constructor(state: RanchState) {
    this.state = state;
  }

  static newGame(seed: number): RanchSim {
    const startPlan = FEED_PLANS.hay;
    const studs = makeStuds(seed >>> 0);
    const rivalRoster = makeRivals(seed >>> 0, 2);
    return new RanchSim({
      meta: { seed: seed >>> 0, tick: 0, day: 1 },
      // The foundation horse arrives at three: just coming good, with a
      // couple of prime seasons ahead — and an end in sight.
      horses: [
        foundationHorse(1, starterName(seed), seasonOf(1) - 3, seed >>> 0),
        ...studs,
        ...rivalRoster.horses,
      ],
      featuredId: 1,
      nextHorseId: rivalRoster.nextId,
      stableLevel: 1,
      yearlings: rollYearlings(seed >>> 0, 1, 0),
      studIds: studs.map((stud) => stud.id),
      rivals: rivalRoster.rivals,
      pendingFoals: [],
      vetLevel: 0,
      patchUpSeason: 0,
      energy: { current: startPlan.energy, max: startPlan.energy },
      economy: { moneyCents: START_MONEY_CENTS, feedPlan: 'hay' },
      facilities: { gallopTrack: 1, enduranceTrail: 1, startingGate: 1, roundPen: 1 },
      entries: [],
      record: { starts: 0, winsByLeague: [0, 0, 0, 0], earningsCents: 0, log: [] },
      racedHorseIdsToday: [],
      raceDoneToday: false,
      drillsToday: 0,
      dailyTrainingCounts: {},
      pendingTraining: null,
    });
  }

  static fromState(state: RanchState): RanchSim {
    return new RanchSim(state);
  }

  enqueue(command: RanchCommand): void {
    this.queue.push(command);
  }

  advance(nowMs: number): ClockFrame {
    return this.clock.advance(nowMs, 1, () => this.stepOnce());
  }

  /** One 10 Hz step: apply queued commands. Exposed for tests. */
  stepOnce(): void {
    this.state.meta.tick += 1;
    for (const command of this.queue.splice(0, this.queue.length)) this.apply(command);
  }

  drainEvents(): RanchEvent[] {
    return this.events.splice(0, this.events.length);
  }

  featured(): Horse | null {
    if (this.state.featuredId === null) return null;
    return this.state.horses.find((horse) => horse.id === this.state.featuredId) ?? null;
  }

  horseById(id: HorseId): Horse | null {
    return this.state.horses.find((horse) => horse.id === id) ?? null;
  }

  barnHorses(): Horse[] {
    return this.state.horses.filter((horse) => horse.location === 'barn');
  }

  barnStalls(): number {
    return stableStalls(this.state.stableLevel);
  }

  entryForEvent(eventId: number) {
    return this.state.entries.find((entry) => entry.eventId === eventId) ?? null;
  }

  /** An entered horse cannot also work on race day. With no id, asks if
   * the stable has any unresolved runner today. */
  hasEnteredRaceToday(horseId?: HorseId): boolean {
    return this.state.entries.some(
      (entry) => eventDay(entry.eventId) === this.state.meta.day && (horseId === undefined || entry.horseId === horseId),
    );
  }

  hasRacedToday(horseId: HorseId): boolean {
    return this.state.racedHorseIdsToday.includes(horseId);
  }

  private trainingKey(horseId: HorseId, facility: FacilityId): string {
    return `${horseId}:${facility}`;
  }

  trainingCountToday(horseId: HorseId, facility: FacilityId): number {
    return this.state.dailyTrainingCounts[this.trainingKey(horseId, facility)] ?? 0;
  }

  trainedToday(horseId: HorseId): boolean {
    const prefix = `${horseId}:`;
    return Object.entries(this.state.dailyTrainingCounts).some(([key, count]) => key.startsWith(prefix) && count > 0);
  }

  nextTrainingMode(facility: FacilityId, horseId = this.state.featuredId): MinigameId | null {
    if (horseId === null) return null;
    const modes = MINIGAMES_FOR_FACILITY[facility];
    const count = this.trainingCountToday(horseId, facility);
    const offset = hashFnv1a(`${this.state.meta.seed}:mode:${this.state.meta.day}:${facility}`) % modes.length;
    return modes[(offset + count) % modes.length]!;
  }

  /** What the featured horse can train to right now: the facility's
   * ceiling, or what age and potential allow — whichever bites first. */
  statCap(stat: StatId): number {
    const def = facilityForStat(stat);
    const level = this.state.facilities[def.id];
    const facilityCap = def.caps[Math.max(0, Math.min(3, level - 1))] ?? STAT_HARD_CAP;
    const horse = this.featured();
    if (!horse) return facilityCap;
    return Math.min(facilityCap, agePotentialCap(horse, stat, this.state.meta.day));
  }

  statCapFor(horse: Horse, stat: StatId): number {
    return this.effectiveCapFor(horse, stat);
  }

  effectiveCapFor(horse: Horse, stat: StatId): number {
    const def = facilityForStat(stat);
    const level = this.state.facilities[def.id];
    const facilityCap = def.caps[Math.max(0, Math.min(3, level - 1))] ?? STAT_HARD_CAP;
    return Math.min(facilityCap, agePotentialCap(horse, stat, this.state.meta.day));
  }

  leagueUnlocked(league: number): boolean {
    const economy = LEAGUE_ECONOMY[league];
    if (!economy) return false;
    if (economy.unlockWins === 0) return true;
    return (this.state.record.winsByLeague[league - 1] ?? 0) >= economy.unlockWins;
  }

  bestLeagueUnlocked(): number {
    let best = 0;
    for (let league = 0; league < LEAGUE_ECONOMY.length; league += 1) {
      if (this.leagueUnlocked(league)) best = league;
    }
    return best;
  }

  private pushEvent(event: RanchEvent): void {
    if (this.events.length >= EVENT_CAP) this.events.shift();
    this.events.push(event);
  }

  private reject(reason: RejectReason): void {
    this.pushEvent({ kind: 'rejected', reason });
  }

  private apply(command: RanchCommand): void {
    switch (command.kind) {
      case 'endDay':
        this.endDay();
        break;
      case 'advanceToNextRace':
        this.advanceToNextRace();
        break;
      case 'setFeedPlan':
        if (FEED_PLANS[command.plan]) {
          this.state.economy.feedPlan = command.plan;
          this.pushEvent({ kind: 'feedPlanSet', plan: command.plan });
        }
        break;
      case 'startTraining':
        if ('facility' in command) this.startTraining(command.facility);
        else this.startTraining(facilityForMinigame(command.minigame), command.minigame);
        break;
      case 'routineTraining':
        this.routineTraining(command.facility);
        break;
      case 'resolveTraining':
        this.resolveTraining(command.tape);
        break;
      case 'abortTraining':
        this.abortTraining();
        break;
      case 'enterRace':
        this.enterRace(command.eventId);
        break;
      case 'withdrawRace':
        this.withdrawRace(command.eventId);
        break;
      case 'applyRaceResult':
        this.applyRaceResult(command.eventId, command.place, command.timeS, command.exhausted ?? 0);
        break;
      case 'upgradeFacility':
        this.upgradeFacility(command.facility);
        break;
      case 'upgradeStable':
        this.upgradeStable();
        break;
      case 'retireHorse':
        this.retireHorse();
        break;
      case 'promoteHorse':
        this.promoteHorse(command.horseId);
        break;
      case 'sellHorse':
        this.sellHorse(command.horseId);
        break;
      case 'buyHorse':
        this.buyHorse(command.lotId);
        break;
      case 'breed':
        this.breed(command.damId, command.sireId);
        break;
      case 'upgradeVet':
        this.upgradeVet();
        break;
      case 'buyBloodlineTest':
        this.buyBloodlineTest(command.horseId);
        break;
      case 'vetPatchUp':
        this.vetPatchUp();
        break;
      case 'renameHorse': {
        const horse = this.featured();
        if (!horse) {
          this.reject('no-horse');
          break;
        }
        const name = sanitizeName(command.name);
        if (!name) this.reject('bad-name');
        else {
          horse.name = name;
          this.pushEvent({ kind: 'renamed', name });
        }
        break;
      }
    }
  }

  private endDay(): void {
    if (this.hasEnteredRaceToday()) {
      this.reject('race-today');
      return;
    }
    this.advanceOneDay(true);
  }

  private advanceToNextRace(): void {
    const state = this.state;
    if (this.hasEnteredRaceToday()) {
      this.reject('race-today');
      return;
    }
    const initialNextDay = state.entries
      .map((entry) => eventDay(entry.eventId))
      .filter((day) => day > state.meta.day)
      .sort((a, b) => a - b)[0];
    if (initialNextDay === undefined) {
      this.reject('no-future-entry');
      return;
    }
    const fromDay = state.meta.day;
    while (!this.hasEnteredRaceToday()) {
      const nextDay = state.entries
        .map((entry) => eventDay(entry.eventId))
        .filter((day) => day > state.meta.day)
        .sort((a, b) => a - b)[0];
      if (nextDay === undefined) break;
      this.advanceOneDay(false);
    }
    this.pushEvent({ kind: 'daysAdvanced', fromDay, toDay: state.meta.day });
    this.pushEvent({ kind: 'dayStarted', day: state.meta.day });
  }

  private advanceOneDay(emitDayStarted: boolean): void {
    const state = this.state;
    const previousSeason = seasonOf(state.meta.day);
    state.meta.day += 1;
    // Stale entries cannot normally exist (endDay blocks), but a hostile
    // save could carry them — drop anything already in the past.
    state.entries = state.entries.filter((entry) => eventDay(entry.eventId) >= state.meta.day);

    // A rollover day's aging is the half-drop; ordinary days bleed one
    // level toward the age cap — "lost a step", then a slow fade.
    if (seasonOf(state.meta.day) > previousSeason) {
      this.rolloverSeason(seasonOf(state.meta.day));
    } else {
      for (const horse of state.horses) {
        for (const stat of ['speed', 'stamina', 'accel', 'heart'] as StatId[]) {
          if (horse.stats[stat] > this.effectiveCapFor(horse, stat)) horse.stats[stat] -= 1;
        }
      }
    }

    // Rest heals: fatigue drains, layoffs count down.
    for (const horse of state.horses) {
      if (horse.location !== 'featured' && horse.location !== 'barn') continue;
      horse.fatigue = Math.max(0, horse.fatigue - 10);
      if (horse.injury) {
        horse.injury.daysLeft -= 1;
        if (horse.injury.daysLeft <= 0) {
          horse.injury = null;
          this.pushEvent({ kind: 'horseHealed', horseId: horse.id });
        }
      }
    }

    let plan = FEED_PLANS[state.economy.feedPlan];
    if (plan.costCents > state.economy.moneyCents) {
      state.economy.feedPlan = 'pasture';
      plan = FEED_PLANS.pasture;
      this.pushEvent({ kind: 'feedDowngraded' });
    }
    state.economy.moneyCents -= plan.costCents;
    state.energy.max = plan.energy;
    state.energy.current = plan.energy;
    state.racedHorseIdsToday = [];
    state.raceDoneToday = false;
    state.drillsToday = 0;
    state.dailyTrainingCounts = {};
    state.pendingTraining = null; // an unfinished session doesn't survive the night
    if (emitDayStarted) this.pushEvent({ kind: 'dayStarted', day: state.meta.day });
  }

  private rolloverSeason(season: number): void {
    const state = this.state;
    for (const horse of state.horses) {
      // Ageing bites: half the excess over the new cap goes immediately —
      // the "lost a step" moment — the rest bleeds daily.
      let lostStep = false;
      for (const stat of ['speed', 'stamina', 'accel', 'heart'] as StatId[]) {
        const cap = this.effectiveCapFor(horse, stat);
        const excess = horse.stats[stat] - cap;
        if (excess > 0) {
          horse.stats[stat] = cap + Math.floor(excess / 2);
          lostStep = true;
        }
      }
      if (lostStep) this.pushEvent({ kind: 'lostAStep', horseId: horse.id });

      if (!horse.retired && ageOf(horse, state.meta.day) >= FORCED_RETIREMENT_AGE) {
        horse.retired = true;
        this.scratchEntriesForHorse(horse.id);
        if (horse.location === 'featured') {
          // Forced retirement always finds a stall, crowded or not.
          horse.location = 'barn';
          state.featuredId = null;
        }
        this.pushEvent({ kind: 'horseRetired', horseId: horse.id, forced: true });
      }
    }
    // Covered mares foal in the night. Foals always find room — a full
    // barn just gets crowded.
    for (const pending of state.pendingFoals) {
      const sire = this.horseById(pending.sireId);
      const dam = this.horseById(pending.damId);
      if (!sire || !dam) continue;
      const foal = conceiveFoal(state.meta.seed, season, sire, dam, state.nextHorseId);
      state.nextHorseId += 1;
      state.horses.push(foal);
      this.pushEvent({ kind: 'foalBorn', horseId: foal.id, name: foal.name });
    }
    state.pendingFoals = [];
    for (const horse of state.horses) horse.bredThisSeason = false;
    this.rolloverRivals(season);
    this.adjustRivalFloors();
    this.pruneRegistry();

    state.yearlings = rollYearlings(state.meta.seed, season, this.bestLeagueUnlocked());
    this.pushEvent({ kind: 'seasonRollover', season });
  }

  /** Rivals campaign offscreen: age, sharpen or fade, win races, and
   * eventually retire — the stallions into the stud book. */
  private rolloverRivals(season: number): void {
    const state = this.state;
    const rng = new DerbyRngV1(hashFnv1a(`${state.meta.seed}:rivalseason:${season}`));
    const nextRoster: typeof state.rivals = [];
    for (const info of state.rivals) {
      const horse = this.horseById(info.horseId);
      if (!horse) continue;
      const age = ageOf(horse, state.meta.day);
      if (age >= FORCED_RETIREMENT_AGE) {
        horse.retired = true;
        if (horse.sex === 'M' && !state.studIds.includes(horse.id)) {
          state.studIds.push(horse.id);
        }
        const replacement = makeReplacementRival(
          state.meta.seed,
          season,
          info.league,
          state.nextHorseId,
          horse,
        );
        state.nextHorseId += 1;
        state.horses.push(replacement.horse);
        nextRoster.push(replacement.info);
        continue;
      }
      // Polish sharpens through the prime years, dulls after.
      info.polish = Math.max(0.75, Math.min(0.97, info.polish + (age <= 5 ? 0.015 : -0.02)));
      horse.stats = rivalStats(horse.potential, age, info.polish);
      // Offscreen campaigning keeps their records alive.
      const offscreenWins = rng.integer(0, 2);
      horse.career.starts += offscreenWins + rng.integer(1, 3);
      horse.career.winsByLeague[info.league] =
        (horse.career.winsByLeague[info.league] ?? 0) + offscreenWins;
      nextRoster.push(info);
    }
    state.rivals = nextRoster;
  }

  /** Snapshot the stable's best specialist for each race shape. Rival
   * floors are then fixed until the next season turnover. */
  private adjustRivalFloors(): void {
    const eligible = this.state.horses.filter(
      (horse) =>
        (horse.location === 'featured' || horse.location === 'barn') &&
        !horse.retired &&
        ageOf(horse, this.state.meta.day) >= 2,
    );
    const specialists = new Map<'sprint' | 'middle' | 'route', Horse>();
    for (const classId of ['sprint', 'middle', 'route'] as const) {
      const distanceM = RACE_CLASSES[classId].distanceM;
      let best: Horse | null = null;
      let bestTime = Number.POSITIVE_INFINITY;
      for (const horse of eligible) {
        const adjusted = classAdjustedStats(horse.stats, classId);
        const time = theoreticalBestTime(statsToParams(adjusted), distanceM);
        if (time < bestTime || (time === bestTime && best !== null && horse.id < best.id)) {
          best = horse;
          bestTime = time;
        }
      }
      if (best) specialists.set(classId, best);
    }
    for (const rival of this.state.rivals) {
      const classId = rival.style === 'sprinter' ? 'sprint' : rival.style === 'closer' ? 'route' : 'middle';
      const benchmark = specialists.get(classId);
      rival.statFloor = benchmark
        ? {
            speed: Math.max(1, Math.round(benchmark.stats.speed * 0.9)),
            stamina: Math.max(1, Math.round(benchmark.stats.stamina * 0.9)),
            accel: Math.max(1, Math.round(benchmark.stats.accel * 0.9)),
            heart: Math.max(1, Math.round(benchmark.stats.heart * 0.9)),
          }
        : { speed: 1, stamina: 1, accel: 1, heart: 1 };
    }
  }

  private scratchEntriesForHorse(horseId: HorseId): void {
    const scratched = this.state.entries.filter((entry) => entry.horseId === horseId);
    if (scratched.length === 0) return;
    this.state.entries = this.state.entries.filter((entry) => entry.horseId !== horseId);
    for (const entry of scratched) {
      this.pushEvent({ kind: 'raceScratched', eventId: entry.eventId, horseId });
    }
  }

  /** Sold horses stay only while the living can still point at them:
   * three generations of ancestry from every owned horse. */
  private pruneRegistry(): void {
    const state = this.state;
    const keep = new Set<HorseId>();
    const walk = (id: HorseId | null, depth: number): void => {
      if (id === null || depth < 0 || keep.has(id)) return;
      keep.add(id);
      const horse = this.horseById(id);
      if (!horse || depth === 0) return;
      walk(horse.sireId, depth - 1);
      walk(horse.damId, depth - 1);
    };
    for (const horse of state.horses) {
      if (horse.location === 'featured' || horse.location === 'barn') walk(horse.id, 3);
    }
    state.horses = state.horses.filter(
      (horse) => horse.location !== 'sold' || keep.has(horse.id),
    );
  }

  /** Traits expressed by a horse's genotype. */
  traitsOf(horse: Horse): TraitId[] {
    return expressedTraits(horse.genotype.traits);
  }

  private revealTraits(horse: Horse, reveal: 'race' | 'training'): void {
    for (const trait of this.traitsOf(horse)) {
      if (TRAIT_DEFS[trait].reveal !== reveal) continue;
      if (horse.revealedTraits.includes(trait)) continue;
      horse.revealedTraits.push(trait);
      this.pushEvent({ kind: 'traitRevealed', horseId: horse.id, trait });
    }
  }

  private breed(damId: HorseId, sireId: HorseId): void {
    const state = this.state;
    if ((state.record.winsByLeague[3] ?? 0) < 1) {
      this.reject('breeding-locked');
      return;
    }
    const dam = this.horseById(damId);
    const day = state.meta.day;
    if (!dam || dam.location !== 'barn' || dam.sex !== 'F' || ageOf(dam, day) < 3) {
      this.reject('bad-mare');
      return;
    }
    if (dam.bredThisSeason || state.pendingFoals.some((pending) => pending.damId === damId)) {
      this.reject('already-bred');
      return;
    }
    const sire = this.horseById(sireId);
    if (!sire || sire.sex !== 'M') {
      this.reject('bad-sire');
      return;
    }
    let feeCents = 0;
    if (sire.location === 'external') {
      feeCents = studFeeCents(sire);
      if (feeCents > state.economy.moneyCents) {
        this.reject('no-money');
        return;
      }
    } else if (sire.location !== 'barn' || ageOf(sire, day) < 3) {
      this.reject('bad-sire');
      return;
    }
    state.economy.moneyCents -= feeCents;
    dam.bredThisSeason = true;
    state.pendingFoals.push({ damId, sireId });
    this.pushEvent({ kind: 'bred', damId, sireId, feeCents });
  }

  private startTraining(facility: FacilityId, legacyMode?: MinigameId): void {
    const state = this.state;
    if (!FACILITY_DEFS[facility]) {
      this.reject('bad-event');
      return;
    }
    const horse = this.featured();
    if (!horse) {
      this.reject('no-horse');
      return;
    }
    if (state.pendingTraining !== null) {
      this.reject('training-pending');
      return;
    }
    if (horse.injury) {
      this.reject('horse-injured');
      return;
    }
    if (this.hasEnteredRaceToday(horse.id)) {
      this.reject('race-day-training');
      return;
    }
    if (this.hasRacedToday(horse.id)) {
      this.reject('horse-raced-today');
      return;
    }
    const minigame = legacyMode ?? this.nextTrainingMode(facility, horse.id);
    if (!minigame) {
      this.reject('bad-event');
      return;
    }
    const def = MINIGAMES[minigame];
    const traits = this.traitsOf(horse);
    const cost = trainingEnergyCost(def.energyCost, traits);
    if (state.energy.current < cost) {
      this.reject('no-energy');
      return;
    }
    state.energy.current -= cost;
    state.drillsToday += 1;
    const key = this.trainingKey(horse.id, facility);
    state.dailyTrainingCounts[key] = (state.dailyTrainingCounts[key] ?? 0) + 1;
    const tier = state.facilities[facility];
    const xpMult = trainingXpMult(traits);
    // Deterministic without consuming RNG state: the command stream alone
    // replays a career, and re-rolling layouts costs a whole session.
    const seed = hashFnv1a(
      `${state.meta.seed}:train:${horse.id}:${minigame}:${state.meta.day}:${state.drillsToday}`,
    );
    state.pendingTraining = {
      horseId: horse.id,
      facility,
      minigame,
      seed,
      tier,
      cost,
      xpMult,
      mods: sessionMods(traits),
    };
    this.pushEvent({
      kind: 'trainingStarted',
      horseId: horse.id,
      minigame,
      seed,
      tier,
      xpMult,
      mods: state.pendingTraining.mods,
    });
  }

  private routineTraining(facility: FacilityId): void {
    const state = this.state;
    if (!FACILITY_DEFS[facility]) {
      this.reject('bad-event');
      return;
    }
    const horse = this.featured();
    if (!horse) {
      this.reject('no-horse');
      return;
    }
    if (state.pendingTraining !== null) {
      this.reject('training-pending');
      return;
    }
    if (horse.injury) {
      this.reject('horse-injured');
      return;
    }
    if (this.hasEnteredRaceToday(horse.id)) {
      this.reject('race-day-training');
      return;
    }
    if (this.hasRacedToday(horse.id)) {
      this.reject('horse-raced-today');
      return;
    }
    const minigame = this.nextTrainingMode(facility, horse.id);
    if (!minigame || !horse.masteredTrainingModes.includes(minigame)) {
      this.reject('training-not-mastered');
      return;
    }
    const def = MINIGAMES[minigame];
    const traits = this.traitsOf(horse);
    const cost = trainingEnergyCost(def.energyCost, traits);
    if (state.energy.current < cost) {
      this.reject('no-energy');
      return;
    }
    state.energy.current -= cost;
    state.drillsToday += 1;
    const key = this.trainingKey(horse.id, facility);
    state.dailyTrainingCounts[key] = (state.dailyTrainingCounts[key] ?? 0) + 1;
    const raw = 0.65;
    const xp = xpFor(raw, trainingXpMult(traits));
    this.applyXpTo(horse, def.stat, xp);
    if (!horse.revealedGrades.includes(def.stat)) horse.revealedGrades.push(def.stat);
    this.revealTraits(horse, 'training');
    this.pushEvent({
      kind: 'trainingResolved',
      horseId: horse.id,
      minigame,
      stat: def.stat,
      xp,
      raw,
      routine: true,
    });
  }

  private resolveTraining(tapeRaw: number[]): void {
    const state = this.state;
    const pending = state.pendingTraining;
    if (!pending) {
      this.reject('no-training');
      return;
    }
    const tape = (Array.isArray(tapeRaw) ? tapeRaw : [])
      .slice(0, 4000)
      .map((mask) => (Number.isInteger(mask) ? (mask as number) & 63 : 0));
    const def = MINIGAMES[pending.minigame];
    const result = runSession(def, pending.tier, pending.seed, tape, pending.mods);
    const horse = this.horseById(pending.horseId);
    const xp = xpFor(result.raw, pending.xpMult);
    state.pendingTraining = null;
    if (horse) this.applyXpTo(horse, def.stat, xp);
    if (horse) {
      if (!horse.revealedGrades.includes(def.stat)) horse.revealedGrades.push(def.stat);
      this.revealTraits(horse, 'training');
      const grade = gradeFor(result.raw);
      if ((grade === 'gold' || grade === 'perfect') && !horse.masteredTrainingModes.includes(pending.minigame)) {
        horse.masteredTrainingModes.push(pending.minigame);
        this.pushEvent({ kind: 'trainingMastered', horseId: horse.id, minigame: pending.minigame });
      }
    }
    this.pushEvent({
      kind: 'trainingResolved',
      horseId: pending.horseId,
      minigame: pending.minigame,
      stat: def.stat,
      xp,
      raw: result.raw,
      routine: false,
    });
  }

  private abortTraining(): void {
    const state = this.state;
    const pending = state.pendingTraining;
    if (!pending) {
      this.reject('no-training');
      return;
    }
    const def = MINIGAMES[pending.minigame];
    const refund = pending.cost ?? def.energyCost;
    state.energy.current = Math.min(state.energy.max, state.energy.current + refund);
    state.drillsToday = Math.max(0, state.drillsToday - 1);
    const key = this.trainingKey(pending.horseId, pending.facility);
    const count = Math.max(0, (state.dailyTrainingCounts[key] ?? 1) - 1);
    if (count === 0) delete state.dailyTrainingCounts[key];
    else state.dailyTrainingCounts[key] = count;
    state.pendingTraining = null;
    this.pushEvent({ kind: 'trainingAborted', minigame: pending.minigame });
  }

  private applyXpTo(horse: Horse, stat: StatId, amount: number): void {
    const cap = this.effectiveCapFor(horse, stat);
    let level = horse.stats[stat];

    if (level >= cap) {
      // At cap: bank at most one level's worth, waste the rest, nudge.
      const banked = Math.min(horse.xp[stat] + amount, xpToNext(level));
      if (banked < horse.xp[stat] + amount) this.pushEvent({ kind: 'capBlocked', stat });
      horse.xp[stat] = banked;
      return;
    }

    horse.xp[stat] += amount;
    while (level < cap && level < STAT_HARD_CAP && horse.xp[stat] >= xpToNext(level)) {
      horse.xp[stat] -= xpToNext(level);
      level += 1;
      this.pushEvent({ kind: 'statLevel', stat, level });
    }
    horse.stats[stat] = level;
    if (level >= cap && horse.xp[stat] > 0) {
      horse.xp[stat] = Math.min(horse.xp[stat], xpToNext(level));
      this.pushEvent({ kind: 'capBlocked', stat });
    }
  }

  private enterRace(id: number): void {
    const state = this.state;
    const horse = this.featured();
    if (!horse) {
      this.reject('no-horse');
      return;
    }
    if (horse.injury) {
      this.reject('horse-injured');
      return;
    }
    if (!isValidEvent(id)) {
      this.reject('bad-event');
      return;
    }
    const day = eventDay(id);
    const league = eventLeague(id);
    const economy = LEAGUE_ECONOMY[league]!;
    if (day < state.meta.day) {
      this.reject('bad-event');
      return;
    }
    if (day > state.meta.day + ENTRY_HORIZON_DAYS) {
      this.reject('too-far-ahead');
      return;
    }
    if (!this.leagueUnlocked(league)) {
      this.reject('locked-league');
      return;
    }
    if (this.entryForEvent(id)) {
      this.reject('already-entered');
      return;
    }
    if (state.entries.some((entry) => entry.horseId === horse.id && eventDay(entry.eventId) === day)) {
      this.reject('horse-entered');
      return;
    }
    if (day === state.meta.day && this.hasRacedToday(horse.id)) {
      this.reject('horse-raced-today');
      return;
    }
    if (day === state.meta.day && this.trainedToday(horse.id)) {
      this.reject('horse-trained-today');
      return;
    }
    if (economy.entryFeeCents > state.economy.moneyCents) {
      this.reject('no-money');
      return;
    }
    state.economy.moneyCents -= economy.entryFeeCents;
    state.entries.push({ eventId: id, horseId: horse.id });
    this.pushEvent({ kind: 'raceEntered', eventId: id, horseId: horse.id });
  }

  private withdrawRace(id: number): void {
    const state = this.state;
    const entry = this.entryForEvent(id);
    if (!entry) {
      this.reject('not-entered');
      return;
    }
    if (eventDay(id) <= state.meta.day) {
      // Day-of you ride (or pull up for a DNF); the fee is committed.
      this.reject('race-day-withdraw');
      return;
    }
    state.entries = state.entries.filter((candidate) => candidate.eventId !== id);
    this.pushEvent({ kind: 'raceWithdrawn', eventId: id, horseId: entry.horseId });
  }

  /** The gamble on the card, in whole percent. Zero shown = zero rolled. */
  injuryRiskPct(horseId: HorseId | null = this.state.featuredId): number {
    const horse = horseId === null ? null : this.horseById(horseId);
    if (!horse || horse.injury) return 0;
    if (horse.fatigue < 40) return 0;
    let risk = Math.min(0.06, (horse.fatigue - 40) * 0.001);
    if (this.traitsOf(horse).includes('ironConstitution')) risk *= 0.5;
    return Math.round(risk * 100);
  }

  private upgradeVet(): void {
    const state = this.state;
    if (state.vetLevel >= 3) {
      this.reject('max-level');
      return;
    }
    const costs = [50_000, 200_000, 600_000];
    const cost = costs[state.vetLevel]!;
    if (cost > state.economy.moneyCents) {
      this.reject('no-money');
      return;
    }
    state.economy.moneyCents -= cost;
    state.vetLevel += 1;
    this.pushEvent({ kind: 'vetUpgraded', level: state.vetLevel });
  }

  private buyBloodlineTest(horseId: HorseId): void {
    const state = this.state;
    if (state.vetLevel < 2) {
      this.reject('no-vet');
      return;
    }
    const horse = this.horseById(horseId);
    if (!horse || (horse.location !== 'featured' && horse.location !== 'barn')) {
      this.reject('not-in-barn');
      return;
    }
    const hidden = this.traitsOf(horse).filter((trait) => !horse.revealedTraits.includes(trait));
    if (15_000 > state.economy.moneyCents) {
      this.reject('no-money');
      return;
    }
    state.economy.moneyCents -= 15_000;
    const found = hidden[0] ?? null;
    if (found) {
      horse.revealedTraits.push(found);
      this.pushEvent({ kind: 'traitRevealed', horseId: horse.id, trait: found });
    }
    this.pushEvent({ kind: 'bloodlineTested', horseId: horse.id, trait: found });
  }

  private vetPatchUp(): void {
    const state = this.state;
    if (state.vetLevel < 3) {
      this.reject('no-vet');
      return;
    }
    const horse = this.featured();
    if (!horse || !horse.injury) {
      this.reject('no-injury');
      return;
    }
    const season = seasonOf(state.meta.day);
    if (state.patchUpSeason === season) {
      this.reject('patch-used');
      return;
    }
    state.patchUpSeason = season;
    horse.injury.daysLeft = Math.max(1, Math.ceil(horse.injury.daysLeft / 2));
    this.pushEvent({ kind: 'patchedUp', horseId: horse.id });
  }

  private applyRaceResult(id: number, place: number, timeS: number | null, exhausted: number): void {
    const state = this.state;
    const entry = this.entryForEvent(id);
    if (!entry || eventDay(id) !== state.meta.day) {
      this.reject('not-entered');
      return;
    }
    const horse = this.horseById(entry.horseId);
    if (!horse) {
      this.reject('no-horse');
      return;
    }
    if (this.hasRacedToday(horse.id)) {
      this.reject('horse-raced-today');
      return;
    }
    const league = eventLeague(id);
    const safePlace = Math.max(1, Math.min(8, Math.round(place)));
    const purse = timeS === null ? 0 : purseFor(league, safePlace);

    state.economy.moneyCents += purse;
    state.record.starts += 1;
    state.record.earningsCents += purse;
    horse.career.starts += 1;
    horse.career.earningsCents += purse;
    if (timeS !== null && safePlace === 1) {
      state.record.winsByLeague[league] = (state.record.winsByLeague[league] ?? 0) + 1;
      horse.career.winsByLeague[league] = (horse.career.winsByLeague[league] ?? 0) + 1;
      const nextLeague = league + 1;
      const nextEconomy = LEAGUE_ECONOMY[nextLeague];
      if (nextEconomy && state.record.winsByLeague[league] === nextEconomy.unlockWins) {
        this.pushEvent({ kind: 'leagueUnlocked', league: nextLeague });
      }
      if (league === DERBY_LEAGUE) this.pushEvent({ kind: 'derbyWon', horseId: horse.id });
    }
    state.record.log.unshift({ day: state.meta.day, horseId: horse.id, league, place: safePlace, timeS, purseCents: purse });
    if (state.record.log.length > RACE_LOG_CAP) state.record.log.length = RACE_LOG_CAP;

    this.revealTraits(horse, 'race');

    // The wear of racing — and the gamble that was printed on the card.
    // The completed entry is gone before an injury scratches that horse's
    // later bookings; this result itself was run, not scratched.
    state.entries = state.entries.filter((candidate) => candidate.eventId !== id);
    const riskPct = this.injuryRiskPct(horse.id);
    if (riskPct > 0) {
      const roll = new DerbyRngV1(hashFnv1a(`${state.meta.seed}:injury:${id}`)).next();
      if (roll < riskPct / 100) {
        const spanRoll = new DerbyRngV1(hashFnv1a(`${state.meta.seed}:layoff:${id}`)).integer(0, 1);
        let days = 2 + spanRoll;
        if (horse.injuriesEver === 0) days = Math.min(days, 2);
        if (state.vetLevel >= 3) days = Math.round(days * 0.6);
        else if (state.vetLevel >= 2) days = Math.round(days * 0.8);
        if (this.traitsOf(horse).includes('ironConstitution')) days -= 1;
        days = Math.max(1, days);
        horse.injury = { daysLeft: days };
        horse.injuriesEver += 1;
        this.scratchEntriesForHorse(horse.id);
        this.pushEvent({ kind: 'horseInjured', horseId: horse.id, days });
      }
    }
    horse.fatigue = Math.min(100, horse.fatigue + 30 + (exhausted > 0 ? 10 : 0));

    state.racedHorseIdsToday.push(horse.id);
    state.raceDoneToday = true;
    this.pushEvent({ kind: 'raceRecorded', eventId: id, horseId: horse.id, place: safePlace, purseCents: purse });
  }

  private upgradeFacility(id: keyof typeof FACILITY_DEFS): void {
    const state = this.state;
    const def = FACILITY_DEFS[id];
    if (!def) {
      this.reject('bad-event');
      return;
    }
    const level = state.facilities[id];
    if (level >= 4) {
      this.reject('max-level');
      return;
    }
    const cost = def.upgradeCostsCents[level - 1]!;
    if (cost > state.economy.moneyCents) {
      this.reject('no-money');
      return;
    }
    state.economy.moneyCents -= cost;
    state.facilities[id] = level + 1;
    this.pushEvent({ kind: 'facilityUpgraded', facility: id, level: level + 1 });
  }

  private upgradeStable(): void {
    const state = this.state;
    if (state.stableLevel >= 4) {
      this.reject('max-level');
      return;
    }
    const cost = STABLE_DEF.upgradeCostsCents[state.stableLevel - 1]!;
    if (cost > state.economy.moneyCents) {
      this.reject('no-money');
      return;
    }
    state.economy.moneyCents -= cost;
    state.stableLevel += 1;
    this.pushEvent({ kind: 'stableUpgraded', level: state.stableLevel });
  }

  private retireHorse(): void {
    const state = this.state;
    const horse = this.featured();
    if (!horse) {
      this.reject('no-horse');
      return;
    }
    if (state.entries.some((entry) => entry.horseId === horse.id)) {
      this.reject('horse-entered');
      return;
    }
    if (this.barnHorses().length >= this.barnStalls()) {
      this.reject('barn-full');
      return;
    }
    horse.location = 'barn';
    horse.retired = true;
    state.featuredId = null;
    this.pushEvent({ kind: 'horseRetired', horseId: horse.id, forced: false });
  }

  private promoteHorse(horseId: HorseId): void {
    const state = this.state;
    const horse = this.horseById(horseId);
    if (!horse || horse.location !== 'barn') {
      this.reject('not-in-barn');
      return;
    }
    if (horse.retired) {
      this.reject('horse-retired');
      return;
    }
    if (ageOf(horse, this.state.meta.day) < 2) {
      this.reject('too-young');
      return;
    }
    const current = this.featured();
    if (current) current.location = 'barn';
    horse.location = 'featured';
    state.featuredId = horse.id;
    this.pushEvent({ kind: 'horsePromoted', horseId: horse.id });
  }

  private sellHorse(horseId: HorseId): void {
    const state = this.state;
    const horse = this.horseById(horseId);
    if (!horse || horse.location !== 'barn') {
      this.reject('not-in-barn');
      return;
    }
    if (state.entries.some((entry) => entry.horseId === horse.id)) {
      this.reject('horse-entered');
      return;
    }
    const price = horseValueCents(horse, state.meta.day);
    state.economy.moneyCents += price;
    // The horse leaves the yard but stays in the book — pedigrees point
    // at it until no living horse remembers.
    horse.location = 'sold';
    this.pushEvent({ kind: 'horseSold', horseId, priceCents: price });
  }

  private buyHorse(lotId: number): void {
    const state = this.state;
    const lot = state.yearlings.find((entry) => entry.lotId === lotId);
    if (!lot) {
      this.reject('bad-lot');
      return;
    }
    const toFeatured = state.featuredId === null;
    if (!toFeatured && this.barnHorses().length >= this.barnStalls()) {
      this.reject('barn-full');
      return;
    }
    if (lot.priceCents > state.economy.moneyCents) {
      this.reject('no-money');
      return;
    }
    state.economy.moneyCents -= lot.priceCents;
    const horse = horseFromGenotype(
      state.nextHorseId,
      lot.name,
      lot.sex,
      seasonOf(state.meta.day) - 2, // debuts at two
      lot.genotype,
      { stats: { ...lot.stats }, location: toFeatured ? 'featured' : 'barn' },
    );
    state.nextHorseId += 1;
    state.horses.push(horse);
    if (toFeatured) state.featuredId = horse.id;
    state.yearlings = state.yearlings.filter((entry) => entry.lotId !== lotId);
    this.pushEvent({ kind: 'horseBought', horseId: horse.id, name: horse.name });
  }
}
